/**
 * RFC 4122 v4 UUID generator with a feature-detect for the browser-
 * native `crypto.randomUUID()`. Falls back to `crypto.getRandomValues()`
 * for WebKit versions that ship `crypto` but no `randomUUID` (iPad
 * Capacitor WebViews on older iOS, some embedded contexts).
 *
 * Used wherever the app needs a stable id minted on the device:
 *   - `client_run_id` on training-session uploads (idempotency key)
 *   - any future "this device created this thing" provenance
 */
export function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    // Absolute last resort — no Crypto API at all. Math.random is not
    // cryptographically strong, but our use of UUIDs here is purely for
    // collision avoidance / idempotency, not for any security claim, so
    // any sufficiently unique 128 bits will do. In practice this branch
    // is unreachable: every Capacitor WebView and modern browser exposes
    // getRandomValues.
    for (let i = 0; i < 16; i++) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }

  // Set the version (4) and variant (10xx) bits per RFC 4122.
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
